import React, {useMemo} from 'react';
import {FiExternalLink} from 'react-icons/fi';
import {applyClubOverrides} from '../../data/clubs';
import {useSiteConfig} from '../../data/ConfigContext';
import {isOwnTeam} from '../../data/games';
import {useLeagueTable} from '../../data/resources';
import {LeagueTableConfig} from '../../types';
import ExternalLink from '../ExternalLink';
import './table.css';

const LeagueTable = ({league}: { league: LeagueTableConfig }) => {
    const config = useSiteConfig();
    const {data, error} = useLeagueTable(league.bew_id);

    const rows = useMemo(
        () => (data && config ? applyClubOverrides(data, config.clubs, league.name) : data),
        [data, config, league.name]);

    const header = (
        <header className="table-card-head">
            <h3 className="table-card-title">{league.name}</h3>
            {league.link && (
                <ExternalLink href={league.link} className="table-card-link">
                    Details <FiExternalLink aria-hidden="true"/>
                </ExternalLink>
            )}
        </header>
    );

    if (!rows) {
        return error ? null : (
            <section className="table-card" aria-busy="true">
                {header}
                <div className="table-card-loading">
                    {Array.from({length: 6}, (_, i) => <div key={i} className="skeleton skeleton-line"/>)}
                </div>
            </section>
        );
    }
    // volleynet returns no rows for unknown competitions or ones that haven't started yet
    if (!rows.length) return null;

    return (
        <section className="table-card">
            {header}
            <table className="league-table">
                <thead>
                <tr>
                    <th className="col-rank"><abbr title="Platz">#</abbr></th>
                    <th className="col-team">Team</th>
                    <th><abbr title="Spiele">Sp</abbr></th>
                    <th><abbr title="Siege">S</abbr></th>
                    <th><abbr title="Niederlagen">N</abbr></th>
                    <th className="col-points"><abbr title="Punkte">Pkt</abbr></th>
                </tr>
                </thead>
                <tbody>
                {rows.map((row, index) => (
                    <tr key={row.name} className={isOwnTeam(row.name) ? 'own-team' : undefined}>
                        <td className="col-rank">{index + 1}</td>
                        <td className={`col-team${row.shortName ? ' has-short-name' : ''}`} title={row.name}>
                            <ExternalLink href={row.link}>
                                <span className="name-full">{row.name}</span>
                                {row.shortName && <span className="name-short">{row.shortName}</span>}
                            </ExternalLink>
                        </td>
                        <td>{row.played}</td>
                        <td>{row.won}</td>
                        <td>{row.lost}</td>
                        <td className="col-points">{row.points}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </section>
    );
};

export default LeagueTable;
